// Path: ./src/app/pages/CreatePreferenceWatchItemPage/index.tsx
import { useEffect, useState } from "react";
import { Button, Grid, Stack } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { adminApiService } from "../../../utils/api";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useLoading } from "../../../contextApi/LoadingContext";
import { toastSuccess } from "../../../utils/pageUtil/toast";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { routes } from "../../../utils/constant/navigationRoutes";
import {
  WatchItemFormState,
  WatchItemRow,
  emptyWatchItemFormState,
  watchItemToFormState,
} from "../../../utils/preference/watchItemPayload";
import { Field, TextControl } from "../../components/FormKit/formControls";
import {
  Callout,
  ControlRow,
  FormActions,
  FormShell,
  PageShell,
  ReadOnlyValue,
  SectionBody,
  SectionBox,
  SectionHeader,
  SectionTitle,
  token,
} from "../../components/FormKit/styled";
import { useHandleSaveWatchItem } from "./handleSaveWatchItem";

const CreatePreferenceWatchItemPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { watchItemId } = useParams<{ watchItemId?: string }>();
  const authHeaders = useAuthHeaders();
  const { showLoadingScreen } = useLoading();
  const handleSaveWatchItem = useHandleSaveWatchItem();

  const [form, setForm] = useState<WatchItemFormState>(
    emptyWatchItemFormState(),
  );
  const [savedRow, setSavedRow] = useState<WatchItemRow>();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isEditing = Boolean(watchItemId);

  useEffect(() => {
    if (!watchItemId) return;

    (async () => {
      showLoadingScreen(true);
      const fetched =
        await adminApiService.getOneBaseGfPreferenceWatchItemAdminControllerGfPreferenceWatchItem(
          watchItemId,
          undefined,
          undefined,
          undefined,
          authHeaders,
        );
      showLoadingScreen(false);

      if (!fetched) return;

      const row = fetched as WatchItemRow;
      setForm(watchItemToFormState(row));
      setSavedRow(row);
    })();
  }, [watchItemId]);

  const setField = <K extends keyof WatchItemFormState>(
    key: K,
    value: WatchItemFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleDelete = async () => {
    if (!watchItemId) return;
    showLoadingScreen(true);
    const result =
      await adminApiService.deleteOneBaseGfPreferenceWatchItemAdminControllerGfPreferenceWatchItem(
        watchItemId,
        authHeaders,
      );
    showLoadingScreen(false);
    if (result === undefined) return;

    setDeleteOpen(false);
    toastSuccess(t(messages.Successfully_Deleted()));
    navigate(routes.preferenceWatchItem.view);
  };

  return (
    <PageShell>
      <AccountCardWrapper
        title={`${isEditing ? t(messages.Edit()) : t(messages.Create())} ${t(
          messages.preference_watch_item(),
        )}`}
      >
        <FormShell data-testid="create-preference-watch-item-page">
          <SectionBox>
            <SectionHeader>
              <SectionTitle>
                {t(messages.preference_section_watch_item())}
              </SectionTitle>
            </SectionHeader>

            <SectionBody>
              <Callout>{t(messages.preference_watch_item_intro())}</Callout>

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Field
                    id="watch-item-name"
                    label={t(messages.preference_watch_item_name())}
                    tooltip={t(messages.preference_tip_watch_item_name())}
                    required
                    help={t(messages.preference_watch_item_name_helper())}
                  >
                    <TextControl
                      id="watch-item-name"
                      value={form.name}
                      placeholder="oat milk"
                      onChange={(event) => setField("name", event.target.value)}
                      inputProps={{ "data-testid": "watch-item-name-input" }}
                    />
                  </Field>
                </Grid>

                {isEditing && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Field
                        label={t(messages.preference_watch_item_normalized())}
                        tooltip={t(
                          messages.preference_watch_item_normalized_helper(),
                        )}
                        help={t(
                          messages.preference_watch_item_normalized_helper(),
                        )}
                      >
                        <ReadOnlyValue data-testid="watch-item-normalized-value">
                          {savedRow?.normalizedName || "—"}
                        </ReadOnlyValue>
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Field
                        label={t(messages.preference_watch_item_curated())}
                        tooltip={t(
                          messages.preference_watch_item_curated_helper(),
                        )}
                        help={t(
                          messages.preference_watch_item_curated_helper(),
                        )}
                      >
                        <ReadOnlyValue data-testid="watch-item-curated-value">
                          {savedRow?.isCurated
                            ? t(messages.preference_watch_item_curated_yes())
                            : t(messages.preference_watch_item_curated_no())}
                        </ReadOnlyValue>
                      </Field>
                    </Grid>
                  </>
                )}
              </Grid>
            </SectionBody>
          </SectionBox>

          <FormActions>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {isEditing && (
                <PermissionComponentWrapper
                  requiredPermission={["delete"]}
                  requiredPermissionGroup={["tag"]}
                >
                  <Button
                    onClick={() => setDeleteOpen(true)}
                    sx={{
                      textTransform: "none",
                      color: token.color.danger,
                      "&:hover": { background: "#fdecec" },
                    }}
                    data-testid="watch-item-delete-button"
                  >
                    {t(messages.Delete())}
                  </Button>
                </PermissionComponentWrapper>
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <ControlRow>
                <Button
                  variant="text"
                  onClick={() => navigate(-1)}
                  sx={{ textTransform: "none", color: token.color.textMuted }}
                  data-testid="watch-item-cancel-button"
                >
                  {t(messages.Cancel())}
                </Button>
              </ControlRow>
              <PermissionComponentWrapper
                requiredPermission={[isEditing ? "edit" : "create"]}
                requiredPermissionGroup={["tag"]}
              >
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => handleSaveWatchItem(form, watchItemId)}
                  sx={{
                    minWidth: 132,
                    height: token.controlHeight,
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: token.radius.sm,
                    backgroundColor: token.color.accent,
                    "&:hover": { backgroundColor: token.color.accentHover },
                  }}
                  data-testid="watch-item-save-button"
                >
                  {isEditing ? t(messages.Update()) : t(messages.Save())}
                </Button>
              </PermissionComponentWrapper>
            </Stack>
          </FormActions>
        </FormShell>
      </AccountCardWrapper>

      <MuiConfirmationModal
        open={deleteOpen}
        handleCancel={() => setDeleteOpen(false)}
        handleConfirm={handleDelete}
        title={t(messages.Delete_Confirmation())}
        description={`${t(messages.Are_you_sure_you_want_to_delete_This())} "${
          savedRow?.name ?? ""
        }"? ${t(messages.This_action_cannot_be_undone())}.`}
      />
    </PageShell>
  );
};

export default CreatePreferenceWatchItemPage;
